import { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { SkipForward } from "react-feather";
import Button from "../../input/Button";
import { getDefaultConfig } from "../../grid/grid-helper";

/*

{
  FIELD: {
    label: string,
    visible: boolean,
    pinned: string,
    customSeq: number,
    width: number
  }
}

*/

const HeaderSelector = ({ close, config, storageKey }) => {
  const [headers, setHeaders] = useState({});
  const [resetPinnedChecked, setResetPinnedChecked] = useState(false);
  const [allSelected, setAllSelected] = useState(true);

  useEffect(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const savedConfig = JSON.parse(saved);
        setHeaders(
          config.reduce((acc, con) => {
            const conf = savedConfig[con.field] ?? {};
            acc[con.field] = {
              ...conf,
              label: con.headerName ?? con.field,
              visible: conf.visible ?? false,
              pinned: conf.pinned,
              customSeq: conf.customSeq,
            };
            return acc;
          }, {}),
        );
      } else {
        setHeaders(
          config.reduce((acc, con, index) => {
            acc[con.field] = {
              label: con.headerName ?? con.field,
              visible: true,
              pinned: con.pinned,
              customSeq: index++,
            };
            return acc;
          }, {}),
        );
      }
    }
  }, [config, storageKey]);

  useEffect(() => {
    if (headers && Object.keys(headers).length > 0) {
      setAllSelected(Object.values(headers).every((header) => header.visible));
    }
  }, [headers]);

  if (!headers || Object.keys(headers).length === 0) {
    return <div>No Config Found</div>;
  }

  const handleProceed = () => {
    localStorage.setItem(storageKey, JSON.stringify(headers));
    if (resetPinnedChecked) {
      const defaultState = getDefaultConfig(config);
      setHeaders(defaultState);
      localStorage.setItem(storageKey, JSON.stringify(defaultState));
      setResetPinnedChecked(false);
    }
    close();
  };

  const toggleHeader = (key) => {
    setHeaders((prevState) => ({
      ...prevState,
      [key]: { ...prevState[key], visible: !prevState[key].visible },
    }));
  };

  const handleSelectAll = (checked) => {
    setHeaders((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([key, field]) => [
          key,
          { ...field, visible: checked },
        ]),
      ),
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    // 1. Build the ordered list of visible headers (by current seq)
    const visibleItems = Object.entries(headers)
      .filter(([, v]) => v.visible)
      .sort((a, b) => a[1].customSeq - b[1].customSeq);

    // 2. Remove dragged item and re-insert at new position
    const [removed] = visibleItems.splice(result.source.index, 1);
    visibleItems.splice(result.destination.index, 0, removed);

    // 3. Re-assign seq, and clear pinned on the dragged header if needed
    const updated = { ...headers };
    visibleItems.forEach(([key], index) => {
      updated[key] = {
        ...updated[key],
        customSeq: index + 1,
        // If this is the item we dragged AND it was pinned → unpin it
        pinned:
          key === removed[0] && updated[key].pinned ? "" : updated[key].pinned,
      };
    });

    setHeaders(updated);
  };

  const visibleHeaders = Object.entries(headers).filter(
    ([_, value]) => value.visible,
  );

  return (
    <div
      style={{
        padding: "10px",
        background: "#fff",
        display: "flex",
        gap: 10,
        height: "90vh",
        overflow: "hidden",
        width: "60vw",
        alignItems: "center",
      }}
    >
      <div style={{ width: "30vw", padding: "10px" }}>
        <h3 style={{ marginTop: "14px" }}>Select Grid Headers</h3>

        <div
          style={{ display: "flex", alignItems: "center", marginBottom: 10 }}
        >
          <input
            type="checkbox"
            checked={allSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
          />
          <span style={{ marginLeft: 8 }}>Select All</span>
        </div>

        <ul
          style={{
            listStyle: "none",
            padding: 0,
            marginBottom: 20,
            overflowY: "auto",
            height: "70vh",
          }}
        >
          {Object.entries(headers).map(([key, con]) => (
            <li
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "6px 0",
                borderBottom: "1px solid #eee",
              }}
            >
              <input
                type="checkbox"
                checked={con.visible || false}
                onChange={() => toggleHeader(key)}
              />
              <span style={{ marginLeft: 8, flex: 1 }}>{con.label}</span>

              {con.visible && (
                <select
                  value={con.pinned || ""}
                  onChange={(e) =>
                    setHeaders((prev) => ({
                      ...prev,
                      [key]: {
                        ...prev[key],
                        pinned: e.target.value,
                      },
                    }))
                  }
                  style={{
                    marginLeft: 8,
                    padding: "4px",
                    fontSize: "12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    marginRight: "5px",
                  }}
                >
                  <option value="">None</option>
                  <option value="left">Pin Left</option>
                  <option value="right">Pin Right</option>
                </select>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div
        style={{
          width: "30vw",
          height: "80vh",
        }}
      >
        <strong style={{ fontSize: "18px" }}>Drag and Drop Headers</strong>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="headers">
            {(provided) => (
              <ul
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  listStyle: "none",
                  padding: 0,
                  overflowY: "auto",
                  height: "70vh",
                }}
              >
                {visibleHeaders
                  .sort(
                    ([aKey, aValue], [bKey, bValue]) =>
                      (aValue.customSeq || 0) - (bValue.customSeq || 0),
                  )
                  .map(([key, con], index) => (
                    <Draggable key={key} draggableId={key} index={index}>
                      {(provided) => (
                        <li
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "8px 12px",
                            marginBottom: 4,
                            background: "#f9f9f9",
                            borderRadius: 4,
                            border: "1px solid #ccc",
                            ...provided.draggableProps.style,
                          }}
                        >
                          <span>{con.label}</span>

                          <span style={{ fontSize: 12, color: "#888" }}>
                            {con.pinned === "left"
                              ? "PINNED LEFT "
                              : con.pinned === "right"
                                ? "PINNED RIGHT "
                                : " "}
                            #{con.customSeq}
                          </span>
                        </li>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
        >
          <div
            style={{ display: "flex", alignItems: "center", margin: "8px 0" }}
          >
            <input
              type="checkbox"
              checked={resetPinnedChecked}
              onChange={(e) => {
                setResetPinnedChecked(e.target.checked);
              }}
            />
            <label style={{ marginLeft: 8 }}>Reset to default</label>
          </div>
          <Button
            label="Ok / Close"
            color="black"
            Icon={SkipForward}
            onClick={handleProceed}
          />
        </div>
      </div>
    </div>
  );
};

export default HeaderSelector;
